import React from "react";

const OverviewPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl md:text-6xl">
              About DTMS
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-primary-100">
              Comprehensive Security Management System Automation Platform
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            DTMS uses an Artificial-Intelligence-enabled web crawler to enhance security and safety. 
            The DTMS app optimizes security management systems and business security operations and 
            automates the detection of security and safety threats to aid efficient security management. 
            The app also enables rapid generation of threat assessments, security risk analysis, real-time 
            security information insights, and security plans to facilitate security operations.
          </p>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Empowering Your Security Management
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            DTMS empowers you by offering a comprehensive security management system automation 
            platform that provides all necessary security reports and geospatial dashboards, 
            ensuring robust visibility and display of threat and risk assessments, and delivers 
            effective security management systems with world-class security intelligence, 
            advisory, and support services in real-time.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            The DTMS app is an automated one-stop shop for security management systems and 
            services. The app handles the full lifecycle of all phases of security management - 
            from threat assessment to security risk assessment and security planning, through an 
            effective online delivery system. We ensure that your security is well-managed.
          </p>
        </div>

        {/* Key Features */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Successful Security Management Every Time
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The DTMS app ensures successful security management every time. We offer a one-stop 
            shop for all security management tools, providing real-time security threat 
            assessments and security advisory support. Our features include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <svg
                className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">User-friendly platform with integrated templates</span>
            </div>
            <div className="flex items-start">
              <svg
                className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Security alerts and automation</span>
            </div>
            <div className="flex items-start">
              <svg
                className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Intuitive functionality powered by a robust billing system</span>
            </div>
            <div className="flex items-start">
              <svg
                className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Customizable security service delivery elements</span>
            </div>
            <div className="flex items-start">
              <svg
                className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Customer collaboration portal</span>
            </div>
            <div className="flex items-start">
              <svg
                className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Comprehensive security reports and geospatial dashboards</span>
            </div>
          </div>
        </div>

        {/* Problem & Solution */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Solving Critical Security Challenges
          </h2>
          <div className="space-y-4">
            <p className="text-lg text-gray-700 leading-relaxed">
              Disconnected elements of security management systems, as well as poorly assessed 
              security threats and risks, cause security vulnerabilities, resulting in loss of 
              revenue and assets. Manually developing security management systems is also 
              time-consuming, which may lead to missed deadlines, budget squeezes, and 
              inconsistent security delivery service quality.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              The DTMS app structures your security management process tasks, optimizes resource 
              utilization, and helps you enhance efficiency. Our innovative platform 
              doesn't just streamline your operations; it frees up your time, allowing you to 
              focus on what matters most – delivering value to your customers.
            </p>
          </div>
        </div>

        {/* Global Reach */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Global Market Presence
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            We currently serve customers across global markets as part of our worldwide expansion. 
            We empower you and your team, drive customer satisfaction, and deliver 
            successful security management system solutions.
          </p>
          <div className="bg-white rounded-lg p-6">
            <p className="text-2xl font-bold text-primary-700 mb-4 text-center">
              Join the DTMS app user ecosystem – Subscribe to our platform.
            </p>
          </div>
        </div>

        {/* Company Info */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Company Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Website</h3>
              <a
                href="https://dtms-espial.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 hover:underline"
              >
                https://dtms-espial.app
              </a>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Established</h3>
              <p className="text-gray-700">April 2024</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Industry</h3>
              <p className="text-gray-700">Security Management System Software Development</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Headquarters</h3>
              <p className="text-gray-700">Winnipeg, Manitoba</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Type</h3>
              <p className="text-gray-700">Privately Held</p>
            </div>
          </div>
          
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Specialties</h3>
            <div className="flex flex-wrap gap-2">
              {[
                "Professional security management system services automation",
                "Security Information/Analysis",
                "Country/State/Province Threat Assessment",
                "Security Risk Assessment",
                "Security Plan",
                "Security Reports",
                "Real-Time Security Alerts and Security Advisory"
              ].map((specialty, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                >
                  {specialty}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
